import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import withTranslation from 'hoc/withTranslation';
import { setAvoidAreaDrawing, cancelAvoidAreaDrawing } from 'actions';
import { colors, fontSize } from '../../global/variables';
import { DRAWER_COLLAPSED_WIDTH } from '../SideBar/constants';

const WIZARD_PANEL_WIDTH = '28rem';

const { black, aqua } = colors;
const { smaller } = fontSize;

const StyledToolbarWrapper = styled.div({
  position: 'fixed',
  top: '7rem',
  left: `calc(var(--sidebar-width, ${DRAWER_COLLAPSED_WIDTH}px) + ${WIZARD_PANEL_WIDTH})`,
  right: '1rem',
  display: 'flex',
  justifyContent: 'center',
  pointerEvents: 'none',
  zIndex: 10200,
  '@media screen and (max-width: 1200px)': {
    left: `calc(var(--sidebar-width, ${DRAWER_COLLAPSED_WIDTH}px) + 1rem)`,
  },
  '@media screen and (max-width: 850px)': {
    top: '4rem',
  },
});

const StyledToolbar = styled.div({
  backgroundColor: black,
  borderRadius: '3rem',
  padding: '0.4rem 0.75rem',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  boxShadow: '0 4px 16px rgba(15,22,33,.3)',
  pointerEvents: 'auto',
});

const StyledHint = styled.span({
  color: 'rgba(227,236,254,.7)',
  fontSize: smaller,
  padding: '0 0.4rem',
  whiteSpace: 'nowrap',
});

const StyledDivider = styled.div({
  width: '1px',
  height: '1rem',
  backgroundColor: 'rgba(255,255,255,.12)',
});

const StyledTool = styled.button(({ active }) => ({
  background: active ? 'rgba(34,205,200,.2)' : 'transparent',
  color: active ? aqua : 'rgba(227,236,254,.5)',
  border: 'none',
  borderRadius: '3rem',
  padding: '0.25rem 0.55rem',
  fontSize: smaller,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  cursor: 'pointer',
  fontFamily: 'FiraGO',
}));

const StyledCancel = styled.button({
  background: 'transparent',
  color: 'rgba(227,236,254,.7)',
  border: 'none',
  borderRadius: '3rem',
  padding: '0.25rem 0.55rem',
  fontSize: smaller,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  cursor: 'pointer',
  fontFamily: 'FiraGO',
  ':hover': { color: '#fff' },
});

const AvoidAreaDrawingToolbar = ({
  translations: {
    wizard: {
      fleet: {
        avoidAreaBboxTab,
        avoidAreaPolygonTab,
        avoidAreaCancelLabel,
        avoidAreaDrawBboxHint,
        avoidAreaDrawPolygonHint,
      },
    },
  },
}) => {
  const dispatch = useDispatch();
  const { active, tool } = useSelector((state) => state.avoidAreaDrawing);

  const onSelectBbox = useCallback(
    () => dispatch(setAvoidAreaDrawing({ tool: 'bbox' })),
    [dispatch],
  );
  const onSelectPolygon = useCallback(
    () => dispatch(setAvoidAreaDrawing({ tool: 'polygon' })),
    [dispatch],
  );
  const onCancel = useCallback(() => dispatch(cancelAvoidAreaDrawing()), [dispatch]);

  if (!active) return null;

  return (
    <StyledToolbarWrapper>
      <StyledToolbar id="avoid-area-drawing-toolbar">
        <StyledTool type="button" active={tool === 'bbox'} onClick={onSelectBbox}>
          {avoidAreaBboxTab}
        </StyledTool>
        <StyledTool type="button" active={tool === 'polygon'} onClick={onSelectPolygon}>
          {avoidAreaPolygonTab}
        </StyledTool>
        <StyledDivider />
        <StyledHint>{tool === 'bbox' ? avoidAreaDrawBboxHint : avoidAreaDrawPolygonHint}</StyledHint>
        <StyledDivider />
        <StyledCancel type="button" id="button-avoid-area-drawing-cancel" onClick={onCancel}>
          {avoidAreaCancelLabel}
        </StyledCancel>
      </StyledToolbar>
    </StyledToolbarWrapper>
  );
};

export default withTranslation(AvoidAreaDrawingToolbar);
